import { useState } from 'react'
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  Package,
  ChevronDown
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

// Mock sales data
const monthlySalesData = [
  { month: 'Jan', sales: 125000, orders: 450, profit: 28000 },
  { month: 'Feb', sales: 145000, orders: 520, profit: 32000 },
  { month: 'Mar', sales: 162000, orders: 580, profit: 38000 },
  { month: 'Apr', sales: 138000, orders: 490, profit: 30000 },
  { month: 'May', sales: 175000, orders: 620, profit: 42000 },
  { month: 'Jun', sales: 198000, orders: 710, profit: 48000 },
  { month: 'Jul', sales: 185000, orders: 665, profit: 44000 },
  { month: 'Aug', sales: 210000, orders: 755, profit: 52000 },
  { month: 'Sep', sales: 225000, orders: 810, profit: 58000 },
  { month: 'Oct', sales: 242000, orders: 870, profit: 62000 },
  { month: 'Nov', sales: 268000, orders: 960, profit: 70000 },
  { month: 'Dec', sales: 285000, orders: 1020, profit: 78000 },
]

const brandSalesData = [
  { name: 'India Gate', sales: 89500, percentage: 25 },
  { name: 'Everest', sales: 71600, percentage: 20 },
  { name: 'Tata', sales: 64440, percentage: 18 },
  { name: 'Haldiram', sales: 53700, percentage: 15 },
  { name: 'MDH', sales: 43000, percentage: 12 },
  { name: 'Others', sales: 35800, percentage: 10 },
]

const productSalesData = [
  { name: 'India Gate Basmati Rice 5kg', sales: 42500, units: 850, trend: 12 },
  { name: 'Everest Garam Masala 100g', sales: 35200, units: 1760, trend: 8 },
  { name: 'Tata Premium Tea 1kg', sales: 28400, units: 710, trend: -3 },
  { name: 'Aashirvaad Atta 10kg', sales: 24500, units: 490, trend: 5 },
  { name: 'Haldiram Aloo Bhujia 400g', sales: 22000, units: 880, trend: 15 },
  { name: 'MDH Chana Masala 100g', sales: 18750, units: 750, trend: -2 },
  { name: 'Tata Sampann Toor Dal 1kg', sales: 16800, units: 560, trend: 7 },
  { name: 'Everest Turmeric Powder 200g', sales: 14400, units: 480, trend: 4 },
]

const departmentSalesData = [
  { name: 'Rice & Grains', value: 98500, color: '#f97316' },
  { name: 'Spices & Masalas', value: 72400, color: '#eab308' },
  { name: 'Dals & Pulses', value: 54200, color: '#22c55e' },
  { name: 'Snacks & Namkeen', value: 45600, color: '#3b82f6' },
  { name: 'Beverages', value: 38800, color: '#8b5cf6' },
  { name: 'Dairy', value: 32500, color: '#ec4899' },
]

const COLORS = ['#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899']

export default function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('year')
  const [selectedBrand, setSelectedBrand] = useState('All Brands')
  const [selectedProduct, setSelectedProduct] = useState('All Products')

  const brands = ['All Brands', 'India Gate', 'Everest', 'Tata', 'Haldiram', 'MDH']
  const products = ['All Products', ...productSalesData.map(p => p.name)]

  const formatCurrency = (value: number) => `₹${(value / 1000).toFixed(0)}K`
  const formatFullCurrency = (value: number) => `₹${value.toLocaleString('en-IN')}`

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Sales Analytics</h1>
          <p className="text-gray-500 mt-1">Track performance and trends across products and brands</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="input-field pl-12 pr-10 appearance-none cursor-pointer"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative">
            <Package className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="input-field pl-12 pr-10 appearance-none cursor-pointer"
            >
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">Total Revenue</span>
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp className="w-4 h-4" /> +18%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-800">₹23,58,000</p>
          <p className="text-sm text-gray-500 mt-1">vs ₹20,00,000 last year</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">Total Orders</span>
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp className="w-4 h-4" /> +24%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-800">8,450</p>
          <p className="text-sm text-gray-500 mt-1">vs 6,820 last year</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">Average Order Value</span>
            <span className="flex items-center gap-1 text-red-600 text-sm font-medium">
              <TrendingDown className="w-4 h-4" /> -4%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-800">₹2,790</p>
          <p className="text-sm text-gray-500 mt-1">vs ₹2,930 last year</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">Profit Margin</span>
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp className="w-4 h-4" /> +2%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-800">24.5%</p>
          <p className="text-sm text-gray-500 mt-1">vs 22.5% last year</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="card p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlySalesData}>
              <defs>
                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={formatCurrency} />
              <Tooltip
                formatter={(value: number) => formatFullCurrency(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #fed7aa',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#f97316"
                strokeWidth={3}
                fill="url(#salesGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sales by Department */}
        <div className="card p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Sales by Department</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={departmentSalesData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={{ stroke: '#d1d5db' }}
              >
                {departmentSalesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => formatFullCurrency(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #fed7aa',
                  borderRadius: '12px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Performance */}
        <div className="card p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Monthly Performance (Orders vs Profit)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlySalesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis yAxisId="left" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={formatCurrency} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #fed7aa',
                  borderRadius: '12px'
                }}
                formatter={(value: number, name: string) => 
                  name === 'profit' ? formatFullCurrency(value) : value
                }
              />
              <Legend />
              <Bar yAxisId="left" dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Orders" />
              <Bar yAxisId="right" dataKey="profit" fill="#22c55e" radius={[4, 4, 0, 0]} name="Profit" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Brand Sales Comparison */}
        <div className="card p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Sales by Brand</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={brandSalesData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={formatCurrency} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} width={100} />
              <Tooltip
                formatter={(value: number) => formatFullCurrency(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #fed7aa',
                  borderRadius: '12px'
                }}
              />
              <Bar dataKey="sales" fill="#f97316" radius={[0, 4, 4, 0]}>
                {brandSalesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Product Sales Table */}
      <div className="card">
        <div className="p-6 border-b border-primary-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-bold text-gray-800">Product Sales Performance</h3>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="input-field pl-12 pr-10 appearance-none cursor-pointer text-sm"
            >
              {products.map(product => (
                <option key={product} value={product}>{product}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">Product</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Total Sales</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Units Sold</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Trend</th>
              </tr>
            </thead>
            <tbody>
              {productSalesData.map((product, index) => (
                <tr key={index} className="table-row">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                        <Package className="w-5 h-5 text-primary-600" />
                      </div>
                      <span className="font-medium text-gray-800">{product.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 font-semibold">
                    {formatFullCurrency(product.sales)}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{product.units.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <div className={`flex items-center gap-1 ${product.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {product.trend >= 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      <span className="font-medium">{product.trend >= 0 ? '+' : ''}{product.trend}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sales Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <h4 className="font-bold text-green-800 mb-2">🏆 Top Performer</h4>
          <p className="text-2xl font-bold text-green-700">India Gate Basmati Rice</p>
          <p className="text-sm text-green-600 mt-1">₹42,500 in sales • +12% growth</p>
        </div>

        <div className="card p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <h4 className="font-bold text-blue-800 mb-2">📈 Fastest Growing</h4>
          <p className="text-2xl font-bold text-blue-700">Haldiram Aloo Bhujia</p>
          <p className="text-sm text-blue-600 mt-1">880 units sold • +15% growth</p>
        </div>

        <div className="card p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <h4 className="font-bold text-orange-800 mb-2">💡 Recommendation</h4>
          <p className="text-lg font-bold text-orange-700">Stock up on Rice & Grains</p>
          <p className="text-sm text-orange-600 mt-1">High demand expected next month</p>
        </div>
      </div>
    </div>
  )
}

